/**
 * Unified Credit Control Dashboard
 * Combines the best elements from Homepage and Dashboard V3
 * Updated: 2026-01-08
 * HMR stability verified
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DataModeBadge } from '@/components/DataModeBadge';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { 
  RefreshCw, 
  Info, 
  TrendingDown, 
  TrendingUp,
  AlertTriangle,
  X,
  CheckCircle2,
  ListChecks,
  CreditCard,
  Target,
  Scale,
  ArrowRight,
  HandCoins,
  ListTodo,
  Receipt,
  Clock,
  MessageSquare
} from 'lucide-react';
import { NavigationMetricCardAlt } from '@/components/NavigationMetricCardAlt';
import { DashboardFilter, type DashboardFilterOption, DashboardSelector } from '@/components/dashboard';
import { useHomepageMetrics } from '@/hooks/useHomepageMetrics';
import { useCollectionQueueSummary } from '@/hooks/useCollectionQueueSummary';
import { usePromisesWidgetSummary } from '@/hooks/usePromisesWidgetSummary';
import { useUnallocatedPaymentsSummary } from '@/hooks/useUnallocatedPaymentsSummary';
import { useDisputesWidgetSummary } from '@/hooks/useDisputesWidgetSummary';
import { useFilteredMetrics } from '@/hooks/useFilteredMetrics';
import { useAgingSummary } from '@/hooks/useAgingSummary';
import { useTodaysProgress, type TodaysProgressData } from '@/hooks/useTodaysProgress';
import { useAuth } from '../../hooks/use-auth';
import { useFilter } from '@/contexts/filter-context';
import { getDefaultFilter } from '@/utils/role-based-content';

// Aging Portfolio data is now calculated from master data based on filter

/** Extracted component to reduce cognitive complexity of the main dashboard */
function TodaysProgressBar({
  progress,
  isLoading,
  isError,
  formatCurrency,
}: Readonly<{
  progress: TodaysProgressData | undefined;
  isLoading: boolean;
  isError: boolean;
  formatCurrency: (value: number) => string;
}>) {
  if (isLoading) {
    return <span className="font-semibold text-foreground">Loading...</span>;
  }

  if (isError) {
    return <span className="text-destructive">Unable to load progress</span>;
  }

  const paymentsCount = progress?.paymentsAllocated.count ?? 0;
  const paymentsAmount = progress?.paymentsAllocated.totalAmount ?? 0;
  const promisesCount = progress?.promisesKept.count ?? 0;
  const casesCount = progress?.casesResolved.count ?? 0;

  return (
    <>
      <span className="font-semibold text-foreground">
        {paymentsCount} payments allocated ({formatCurrency(paymentsAmount)})
      </span>
      {' \u2022 '}
      <span className="font-semibold text-foreground">{promisesCount} promises kept</span>
      {' \u2022 '}
      <span className="font-semibold text-foreground">{casesCount} cases resolved</span>
    </>
  );
}

export function UnifiedCreditControlDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setCurrentFilter } = useFilter();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<DashboardFilterOption>(() => {
    const defaultFilterId = getDefaultFilter(user.role, user.id);
    console.log('[Dashboard] User:', user.name, 'Role:', user.role, 'Default filter ID:', defaultFilterId);
    
    // Find the matching filter option
    const filterOptions: DashboardFilterOption[] = [
      { id: 'everyone', name: 'Manager view', type: 'all' },
      { id: user.id, name: user.name, type: 'user' },
    ];
    
    const defaultOption = filterOptions.find(opt => opt.id === defaultFilterId) || filterOptions[0];
    console.log('[Dashboard] Selected filter option:', defaultOption);
    return defaultOption;
  });

  // Sync local filter state with global filter context
  // This allows DashboardSelector to react to filter changes
  useEffect(() => {
    const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
    setCurrentFilter(filterId);
    console.log('[Dashboard] Filter changed to:', filterId);
  }, [selectedFilter, setCurrentFilter]);

  // Get metrics from centralized data store (for wins and key metrics)
  const { todaysWins, keyMetrics } = useHomepageMetrics();

  // Get collection queue summary (uses live API in live mode, mock data in demo mode)
  // When a specific user is selected, pass their ID so the API filters by assignedTo
  const selectedUserId = selectedFilter.id !== 'everyone' ? selectedFilter.id : undefined;
  const { data: queueSummary, isLoading: isQueueSummaryLoading } = useCollectionQueueSummary(selectedUserId);
  
  // Get promises widget summary (uses live API in live mode, mock data in demo mode)
  const { data: promisesWidgetSummary, isLoading: isPromisesWidgetLoading } = usePromisesWidgetSummary(selectedUserId);

  // Get unallocated payments summary (uses live API in live mode, mock data in demo mode)
  const { data: unallocatedSummary, isLoading: isUnallocatedLoading } = useUnallocatedPaymentsSummary();

  // Get today's progress (payments allocated, promises kept, cases resolved)
  const { data: todaysProgress, isLoading: isTodaysProgressLoading, isError: isTodaysProgressError } = useTodaysProgress(selectedUserId);
  // Get disputes widget summary (uses live API in live mode, mock data in demo mode)
  const { data: disputesWidgetSummary, isLoading: isDisputesWidgetLoading } = useDisputesWidgetSummary(selectedUserId);

  // Get filtered metrics based on selected filter
  const filterId = selectedFilter.id === 'everyone' ? 'everyone' : selectedFilter.id;
  const filteredMetrics = useFilteredMetrics(filterId);

  // Get aging summary (uses live API in live mode, mock data in demo mode)
  const { data: agingSummary, isLoading: isAgingSummaryLoading, isError: isAgingSummaryError } = useAgingSummary();

  // Filter suffix for section headers
  const filterSuffix = selectedFilter.id !== 'everyone' ? ` - ${selectedFilter.name}` : '';

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Map the filtered metrics to the format expected by the dashboard
  const masterPriorities = {
    collectionQueue: {
      count: filteredMetrics.todaysPriorities.casesDueToday.count,
      amount: filteredMetrics.todaysPriorities.casesDueToday.value,
    },
    unallocatedPayments: {
      count: filteredMetrics.todaysPriorities.unallocatedReceipts.count,
      amount: filteredMetrics.todaysPriorities.unallocatedReceipts.value,
    },
    promisesDueToday: {
      dueCount: filteredMetrics.todaysPriorities.promisesDueToday.count,
      brokenCount: filteredMetrics.todaysPriorities.promisesDueToday.brokenCount || 0,
      dueAmount: filteredMetrics.todaysPriorities.promisesDueToday.value,
    },
    disputes: {
      count: filteredMetrics.todaysPriorities.disputesRequiringResponse.count,
      amount: filteredMetrics.todaysPriorities.disputesRequiringResponse.value,
    },
  };
  
  // Calculate portfolio overview data for Performance Snapshot
  const aging = filteredMetrics.agingPortfolio;
  const over90Amount = aging.over91120 + aging.over121180;
  const over90Percentage = aging.total > 0 ? Math.round((over90Amount / aging.total) * 100) : 0;
  const portfolioOverview = {
    totalOutstanding: aging.total,
    customerCount: filteredMetrics.keyMetrics.customerCount,
    over90Amount,
    over90Percentage
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6 space-y-6">
        {/* Header Controls - Single Row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Dashboard Toggle Buttons */}
            <DashboardSelector />
            
            {/* Refresh Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>

          {/* Filter Dropdown */}
          <DashboardFilter
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
          />
        </div>

        {/* Global Filter Banner */}
        {selectedFilter.id !== 'everyone' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-semibold">
                {selectedFilter.initials || selectedFilter.name.substring(0, 2).toUpperCase()}
              </div>
              <span className="text-sm font-medium text-gray-900">
                Viewing: {selectedFilter.name}'s Portfolio
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedFilter({ id: 'everyone', name: 'Manager view', type: 'all' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Dashboard Title */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">Credit Control Dashboard</h1>
            <DataModeBadge />
          </div>
          <p className="text-sm text-gray-600">
            Real-time overview of outstanding invoices, collection performance, and team activity
          </p>
          <p className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Section 1: Today's Priorities - 4 Gateway Scorecards */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal-600" />
            Today's Priorities
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Collection Queue */}
            <NavigationMetricCardAlt
              title="Collection Queue"
              value={isQueueSummaryLoading ? '...' : (queueSummary?.collectionQueueCount ?? masterPriorities.collectionQueue.count).toString()}
              description="Accounts requiring action"
              secondaryValue={isQueueSummaryLoading ? '...' : formatCurrency(queueSummary?.accountsRequiringActionTotal ?? masterPriorities.collectionQueue.amount)}
              icon={Target}
              variant="info"
              onClick={() => navigate('/collections/queue')}
            />

            {/* Unallocated Payments */}
            <NavigationMetricCardAlt
              title="Unallocated Payments"
              value={isUnallocatedLoading ? '...' : (unallocatedSummary?.unallocatedCount ?? masterPriorities.unallocatedPayments.count).toString()}
              description="Receipts pending"
              secondaryValue={isUnallocatedLoading ? '...' : formatCurrency(unallocatedSummary?.totalOutstandingAmount ?? masterPriorities.unallocatedPayments.amount)}
              icon={CreditCard}
              variant="info"
              onClick={() => navigate('/payments/receipts?filter=unallocated')}
            />

            {/* Promises Due Today */}
            <NavigationMetricCardAlt
              title="Promises Due Today"
              value={isPromisesWidgetLoading ? '...' : (promisesWidgetSummary?.promisesDueToday.count ?? masterPriorities.promisesDueToday.dueCount).toString()}
              description="Broken Promises"
              secondaryValue={isPromisesWidgetLoading ? '...' : `${promisesWidgetSummary?.brokenPromises.count ?? masterPriorities.promisesDueToday.brokenCount} | ${formatCurrency(promisesWidgetSummary?.brokenPromises.totalAmount ?? masterPriorities.promisesDueToday.dueAmount)}`}
              icon={HandCoins}
              variant="info"
              onClick={() => navigate('/collections/promises?filter=due-today')}
            />

            {/* Disputes */}
            <NavigationMetricCardAlt
              title="Disputes"
              value={isDisputesWidgetLoading ? '...' : (disputesWidgetSummary?.unresolvedCount ?? masterPriorities.disputes.count).toString()}
              description="Unresolved Disputes"
              secondaryValue={isDisputesWidgetLoading ? '...' : formatCurrency(disputesWidgetSummary?.unresolvedTotalAmount ?? masterPriorities.disputes.amount)}
              icon={Scale}
              variant="info"
              onClick={() => navigate('/disputes?filter=active')}
            />
          </div>

          {/* Today's Progress Summary */}
          <div className="mt-4 pt-4 border-t border-border text-center text-sm text-muted-foreground">
            <CheckCircle2 className="inline-block h-4 w-4 text-green-600 mr-2 -mt-0.5" />
            Today&apos;s progress:{' '}
            <TodaysProgressBar
              progress={todaysProgress}
              isLoading={isTodaysProgressLoading}
              isError={isTodaysProgressError}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>

        {/* Section 2: Ageing Analysis + Performance Snapshot (Side by Side) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Ageing Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Ageing Analysis{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isAgingSummaryLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="space-y-2 animate-pulse">
                      <div className="flex items-center justify-between">
                        <div className="h-4 w-32 bg-gray-200 rounded" />
                        <div className="h-5 w-24 bg-gray-200 rounded" />
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" />
                    </div>
                  ))}
                </div>
              ) : isAgingSummaryError ? (
                <div className="text-sm text-destructive">Unable to load ageing analysis data</div>
              ) : agingSummary ? (
                <>
                  {agingSummary.bands.map((band) => {
                    const maxPercentage = Math.max(...agingSummary.bands.map((b) => b.percentage));
                    const barWidth = maxPercentage > 0 ? (band.percentage / maxPercentage) * 100 : 0;
                    return (
                      <Link
                        key={band.agingBandId}
                        to={`/analytics-reporting/ageing-analysis?filter=${band.bandLabel}`}
                        className="block group"
                      >
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">{band.bandLabel}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-900">
                                {formatCurrency(band.outstandingAmount)}
                              </span>
                              <span className="text-sm text-gray-500">({Math.round(band.percentage)}%)</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-teal-500 h-2 rounded-full transition-all group-hover:bg-teal-600"
                              style={{ width: `${barWidth}%` }}
                            />
                          </div>
                        </div>
                      </Link>
                    );
                  })}

                  <div className="pt-4 mt-5 border-t border-border">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Total outstanding</span>
                      <span className="text-2xl font-bold text-foreground">
                        {formatCurrency(agingSummary.totalOutstanding)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/analytics-reporting/ageing-analysis')}
                      className="w-full justify-between text-neutral-700 hover:text-teal-600 hover:bg-teal-50"
                    >
                      <span className="text-sm font-medium">See your aging debt</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>

          {/* Performance Snapshot - Redesigned */}
          <Card>
            <CardHeader>
              <CardTitle>Performance Snapshot{filterSuffix}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Section 1: Key Metrics */}
              <div className="pb-3 border-b border-border">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                  Key Metrics
                </p>
                <div className="grid grid-cols-4 gap-5">
                  <div>
                    <p className="text-base text-foreground mb-2">Customers</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{portfolioOverview.customerCount}</p>
                    <p className="text-sm text-muted-foreground">
                      Total customers
                    </p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">DSO</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.dso.current}</p>
                    <p className="text-sm text-muted-foreground">
                      Target: {keyMetrics.dso.target} days
                    </p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">Promise Success</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.promiseSuccessRate}%</p>
                    <p className="text-sm text-muted-foreground">21 of 40 kept</p>
                  </div>
                  <div>
                    <p className="text-base text-foreground mb-2">Avg Payment Days</p>
                    <p className="text-2xl font-bold text-foreground leading-none mb-3">{keyMetrics.avgPaymentDays}</p>
                    <p className="text-sm text-muted-foreground">3 days faster</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Collection Rate - Donut Chart */}
              <div className="flex flex-col items-center">
                <div className="flex items-center gap-2 mb-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Collection Rate
                  </p>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <div className="space-y-2">
                          <p className="font-semibold">Collection Rate Calculation:</p>
                          <p className="text-sm">Cash collected this month ÷ Total cash due this month</p>
                          <p className="text-sm">£1.8M ÷ £4.2M = 43%</p>
                          <p className="text-sm font-semibold mt-2">Target: 85% (industry benchmark)</p>
                          <p className="text-sm">Current gap: 42 percentage points below target</p>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                
                {/* Donut Chart */}
                <div className="relative w-[200px] h-[200px] my-3 flex items-center justify-center">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={[
                        { value: todaysWins.collectionRate },
                        { value: 100 - todaysWins.collectionRate }
                      ]}
                      cx={100}
                      cy={100}
                      innerRadius={72}
                      outerRadius={90}
                      startAngle={90}
                      endAngle={450}
                      dataKey="value"
                      stroke="none"
                    >
                      <Cell fill="oklch(0.5 0.09 194.77)" />
                      <Cell fill="oklch(0.9499 0.0059 239.82)" />
                    </Pie>
                  </PieChart>
                  
                  {/* Center Text */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <p className="text-4xl font-bold text-foreground leading-none">
                      {todaysWins.collectionRate}%
                    </p>
                  </div>
                </div>
                
                <p className="text-[13px] text-muted-foreground text-center flex items-center gap-1">
                  Target: {todaysWins.target}% • <span className="text-warning-600 font-medium">{todaysWins.target - todaysWins.collectionRate} points below target</span>
                  <AlertTriangle className="h-3 w-3 text-warning-500" />
                </p>
              </div>

            </CardContent>
          </Card>
        </div>


      </div>
    </div>
  );
}
